'use client';

import React, { useEffect, useState } from 'react';
import { Breadcrumbs } from '@/components/Navigation/Breadcrumbs';
import { FAQSection } from '@/components/Content/FAQSection';
import { RelatedContent } from '@/components/Content/RelatedContent';
import { BlogSidebar } from '@/components/blog/BlogSidebar';
import { AuthorBio } from '@/components/blog/AuthorBio';
import { ReadingProgress } from '@/components/blog/ReadingProgress';
import { BlogContentWrapper } from '@/components/blog/BlogContentWrapper';
import { SocialShare } from '@/components/blog/SocialShare';
import { Badge } from '@/components/ui/badge';
import { ButtonCta } from '@/components/ui/ButtonCta';
import { Calendar, Clock, TrendingUp, User } from '@/lib/icons';
import { getAuthorProfile } from '@/lib/author';

interface BlogPostTemplateProps {
  content: {
    meta: any;
    content: string;
    compiledContent?: React.ReactElement | null;
    timeToRead?: string;
    wordCount?: number;
  };
  breadcrumbs: Array<{ name: string; url: string }>;
  fullSlug: string;
  allBlogPosts?: Array<{
    title: string;
    slug: string;
    category: string;
  }>;
}

export function BlogPostTemplate({ 
  content, 
  breadcrumbs,
  fullSlug,
  allBlogPosts = []
}: BlogPostTemplateProps) {
  // DEFENSIVE: Ensure content exists and has required properties
  if (!content) {
    console.error('[BlogPostTemplate] No content provided for:', fullSlug);
    return (
      <div className="min-h-screen bg-white p-8">
        <h1 className="text-2xl font-bold text-red-600">Error: Content not loaded</h1>
        <p>Please refresh the page or contact support.</p>
      </div>
    );
  }
  
  const { meta = {}, compiledContent, timeToRead, wordCount } = content;
  const [readingProgress, setReadingProgress] = useState(0);
  const [showSidebar, setShowSidebar] = useState(true);

  // Known authors (currently Andrew) get a linked byline to their on-site bio
  // page — the visible half of the author-entity work; the schema half lives
  // in SchemaMarkup's Article/Person nodes.
  const authorProfile = getAuthorProfile(meta.author);

  // Note: InContentCTA components are now directly embedded in MDX files
  // This provides better control and SEO optimization

  // Track reading progress
  useEffect(() => {
    // DEFENSIVE: Check if we're in browser environment
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    
    const handleScroll = () => {
      const winScroll = document.documentElement.scrollTop;
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled = (winScroll / height) * 100;
      setReadingProgress(Math.min(100, Math.max(0, scrolled)));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Format publish date
  const formattedDate = meta.publishedTime 
    ? new Date(meta.publishedTime).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : '';

  // Format modified date
  const formattedModified = meta.modifiedTime && meta.modifiedTime !== meta.publishedTime
    ? new Date(meta.modifiedTime).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : null;

  return (
    <>
      {/* Reading Progress Bar */}
      <ReadingProgress progress={readingProgress} />

      <article className="min-h-screen bg-white">
        {/* Breadcrumbs */}
        <div className="border-b border-gray-100">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <Breadcrumbs items={breadcrumbs} />
          </div>
        </div>

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/5 via-white to-primary/5 py-12 sm:py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              {/* Category Badge */}
              <div className="flex items-center justify-center mb-6">
                <TrendingUp className="w-5 h-5 text-primary mr-2" />
                <Badge variant="outline" className="text-sm font-medium">
                  {meta.category || 'Mortgage Marketing'}
                </Badge>
              </div>

              {/* Title */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 text-center mb-6 leading-tight">
                {meta.h1 || meta.title}
              </h1>

              {/* Subtitle if exists */}
              {meta.subHeadline && (
                <p className="text-xl text-gray-600 text-center mb-8 max-w-3xl mx-auto">
                  {meta.subHeadline}
                </p>
              )}

              {/* Meta Information */}
              <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-600">
                {/* Author */}
                <div className="flex items-center">
                  <User className="w-4 h-4 mr-1" />
                  {authorProfile ? (
                    <a
                      href={authorProfile.pagePath}
                      className="font-medium text-gray-900 hover:text-primary transition-colors"
                      title={`About ${authorProfile.name}`}
                    >
                      {meta.author}
                    </a>
                  ) : (
                    <span>{meta.author || 'LeadPops Team'}</span>
                  )}
                </div>

                {/* Publish Date */}
                {formattedDate && (
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    <time dateTime={meta.publishedTime}>{formattedDate}</time>
                  </div>
                )}

                {/* Reading Time */}
                {timeToRead && (
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    <span>{timeToRead}</span>
                  </div>
                )}

                {/* Updated Date */}
                {formattedModified && (
                  <div className="flex items-center text-primary">
                    <span className="font-medium">Updated: {formattedModified}</span>
                  </div>
                )}
              </div>

              {/* Social Share - Hidden */}
              <div className="mt-4 hidden">
                <SocialShare
                  url={`https://leadpops.com/blog/${fullSlug.replace('blog/', '')}`}
                  title={meta.title || 'LeadPops Blog Post'}
                  description={meta.description}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Hero Image (if set via admin) */}
        {meta.heroImageUrl && (
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-2">
            <div className="max-w-4xl mx-auto">
              <img
                src={meta.heroImageUrl}
                alt={meta.h1 || meta.title}
                className="w-full h-64 object-cover rounded-2xl shadow-md"
                loading="eager"
              />
            </div>
          </div>
        )}

        {/* Main Content Area with Sidebar */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col lg:flex-row gap-12 max-w-7xl mx-auto">
            {/* Main Content */}
            <div className="flex-1 max-w-4xl">
              {/* Article Content */}
              <BlogContentWrapper
                compiledContent={compiledContent}
                fallbackContent={content.content}
                slug={fullSlug}
              />

              {/* Author Bio */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <AuthorBio
                  author={meta.author || 'Andrew Pawlak'}
                  authorImage={meta.authorImage || '/Andrew-Pawlak-LeadPops-rebeliQ.jpeg'}
                  authorBio={meta.authorBio || authorProfile?.bioShort}
                  authorTitle={authorProfile?.title}
                  authorPage={authorProfile?.pagePath}
                  authorLinkedIn={authorProfile?.linkedin}
                />
              </div>

              {/* FAQ Section */}
              {meta.faqs && meta.faqs.length > 0 && (
                <FAQSection faqs={meta.faqs} />
              )}

              {/* Related Content - DIAGNOSTIC: Commented out for bisection */}
              {/* {meta.relatedPages && meta.relatedPages.length > 0 && (
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Related Resources</h2>
                  <RelatedContent pages={meta.relatedPages} />
                </div>
              )} */}
            </div>

            {/* Sidebar - Fixed on Desktop */}
            <aside className="lg:w-80 lg:sticky lg:top-24 lg:h-fit">
              <BlogSidebar 
                currentSlug={fullSlug}
                category={meta.category}
                allBlogPosts={allBlogPosts}
                ctaHeadline={meta.sidebarCtaHeadline}
                ctaDescription={meta.sidebarCtaDescription}
                ctaButtonText={meta.sidebarCtaButtonText}
                ctaButtonLink={meta.sidebarCtaButtonLink}
                ctaSubtext={meta.sidebarCtaSubtext}
              />
            </aside>
          </div>
        </div>

        {/* Bottom CTA Section */}
        <section className="bg-primary py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Your Competition Is Already Doing This
              </h2>
              <p className="text-xl text-white/90 mb-8">
                While you're reading this, they're generating exclusive leads. When do you start?
              </p>
              <ButtonCta
                href="/mortgage-lead-generation-demo"
                variant="primary"
                showArrow={true}
              >
                Book Your Demo Now
              </ButtonCta>
              <p className="text-sm text-white/70 mt-6">
                See the actual system • No theory
              </p>
            </div>
          </div>
        </section>
      </article>
    </>
  );
}